import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { trackEvent } from '@/lib/analytics';
import { consumeAuthIntent } from '@/lib/authIntent';
import { clearFreeOcrEntitlement } from '@/lib/ocrEntitlement';

// Module-level dedupe across StrictMode double-mount and multiple useAuth consumers.
const seenSessions = new Set<string>();

function emitSignInEvent(session: Session) {
  const token = session.access_token;
  if (!token || seenSessions.has(token)) return;
  seenSessions.add(token);

  // Deduped with the sign-in event: the guest entitlement counter no longer
  // applies once registered (daily limits take over).
  // NOTE: `guest_auth_completed` is deliberately NOT emitted here. Ordinary
  // signup/login is not a signup-gate conversion; that event is emitted only
  // from a valid gate-created pending OCR (see src/pages/Scan.tsx).
  clearFreeOcrEntitlement();

  const provider =
    (session.user?.app_metadata as { provider?: string } | undefined)?.provider ??
    'email';

  // Prefer explicit signal from the email signup path.
  const explicit = consumeAuthIntent();
  if (explicit === 'signup') {
    trackEvent('signup_success', { provider });
    return;
  }
  if (explicit === 'login') {
    trackEvent('login_success', { provider });
    return;
  }

  // Fallback heuristic for OAuth / reopened tabs.
  const createdAt = session.user?.created_at
    ? new Date(session.user.created_at).getTime()
    : 0;
  const isFresh = createdAt > 0 && Date.now() - createdAt < 60_000;
  trackEvent(isFresh ? 'signup_success' : 'login_success', { provider });
}

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        if (event === 'SIGNED_IN' && session) {
          emitSignInEvent(session);
        } else if (event === 'SIGNED_OUT') {
          seenSessions.clear();
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return { user, session, loading, signOut };
};
